import {
  isRecord,
  pickFirstNonEmpty,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type { MetricBlueprint } from "@/lib/ai/visualization/data/adapters/metric-binding";
import { readFirstPathNumber } from "@/lib/ai/visualization/data/adapters/metric-binding";
import type { VizDatasetRow } from "@/lib/ai/visualization/data/schema";

const UA_GROUP_BY_VALUES = [
  "FIRST_PAGE_LOCATION_URL_GROUP",
  "SECOND_PAGE_LOCATION_URL_GROUP",
  "FIRST_PAGE_TITLE",
  "SECOND_PAGE_TITLE",
  "SOURCE_MEDIUM",
  "CAMPAIGN",
  "DEFAULT_CHANNEL_GROUP",
  "EXP_VARIANT_STRING",
] as const;

/**
 * UA aggregated の groupBy として adapter が扱う許可値。
 */
export type UaGroupBy = (typeof UA_GROUP_BY_VALUES)[number];

function isUaGroupBy(value: unknown): value is UaGroupBy {
  return (
    typeof value === "string" &&
    (UA_GROUP_BY_VALUES as readonly string[]).includes(value)
  );
}

function resolveUaGroupByFromOutput(
  output: Record<string, unknown>,
): UaGroupBy | null {
  const applied = isRecord(output.applied) ? output.applied : null;
  const groupBy = applied?.groupBy;
  return isUaGroupBy(groupBy) ? groupBy : null;
}

function inferUaGroupBy(items: unknown[]): UaGroupBy | null {
  const checks: ReadonlyArray<{
    value: UaGroupBy;
    match: (item: unknown) => boolean;
  }> = [
    {
      value: "SOURCE_MEDIUM",
      match: (item) => isRecord(item) && isRecord(item.sourceMedium),
    },
    {
      value: "CAMPAIGN",
      match: (item) => isRecord(item) && typeof item.campaign === "string",
    },
    {
      value: "DEFAULT_CHANNEL_GROUP",
      match: (item) =>
        isRecord(item) && typeof item.defaultChannelGroup === "string",
    },
    {
      value: "FIRST_PAGE_LOCATION_URL_GROUP",
      match: (item) =>
        isRecord(item) && isRecord(item.firstPageLocationURLGroup),
    },
    {
      value: "SECOND_PAGE_LOCATION_URL_GROUP",
      match: (item) =>
        isRecord(item) && isRecord(item.secondPageLocationURLGroup),
    },
    {
      value: "FIRST_PAGE_TITLE",
      match: (item) =>
        isRecord(item) && typeof item.firstPageTitle === "string",
    },
    {
      value: "SECOND_PAGE_TITLE",
      match: (item) =>
        isRecord(item) && typeof item.secondPageTitle === "string",
    },
    {
      value: "EXP_VARIANT_STRING",
      match: (item) =>
        isRecord(item) && typeof item.expVariantString === "string",
    },
  ];

  for (const check of checks) {
    if (items.some(check.match)) return check.value;
  }
  return null;
}

/**
 * UA aggregated 出力と grouped 系配列から groupBy 軸を解決する。
 *
 * applied.groupBy を最優先し、欠落時のみ row shape 推論へフォールバックする。
 */
export function resolveUaGroupBy(args: {
  output: Record<string, unknown>;
  grouped: unknown[];
  comparingGrouped: unknown[];
}): UaGroupBy | null {
  const fromOutput = resolveUaGroupByFromOutput(args.output);
  if (fromOutput) return fromOutput;

  const candidates =
    args.grouped.length > 0 ? args.grouped : args.comparingGrouped;
  return inferUaGroupBy(candidates);
}

function getSourceMediumLabel(item: Record<string, unknown>): string | null {
  const sourceMedium = isRecord(item.sourceMedium) ? item.sourceMedium : null;
  const source = sourceMedium ? toString(sourceMedium.source) : null;
  const medium = sourceMedium ? toString(sourceMedium.medium) : null;
  return source || medium
    ? `${source ?? "(direct)"} / ${medium ?? "(none)"}`
    : null;
}

function getUaGroupLabel(
  item: Record<string, unknown>,
  groupBy: UaGroupBy | null,
): string | null {
  const defaultChannelGroup = toString(item.defaultChannelGroup);
  const campaign = toString(item.campaign);
  const firstUrlGroup = isRecord(item.firstPageLocationURLGroup)
    ? toString(
        item.firstPageLocationURLGroup.name ??
          item.firstPageLocationURLGroup.id,
      )
    : null;
  const secondUrlGroup = isRecord(item.secondPageLocationURLGroup)
    ? toString(
        item.secondPageLocationURLGroup.name ??
          item.secondPageLocationURLGroup.id,
      )
    : null;
  const firstTitle = toString(item.firstPageTitle);
  const secondTitle = toString(item.secondPageTitle);
  const expVariant = toString(item.expVariantString);

  if (groupBy === "SOURCE_MEDIUM") return getSourceMediumLabel(item);
  if (groupBy === "CAMPAIGN") return campaign;
  if (groupBy === "DEFAULT_CHANNEL_GROUP") return defaultChannelGroup;
  if (groupBy === "FIRST_PAGE_LOCATION_URL_GROUP") return firstUrlGroup;
  if (groupBy === "SECOND_PAGE_LOCATION_URL_GROUP") return secondUrlGroup;
  if (groupBy === "FIRST_PAGE_TITLE") return firstTitle;
  if (groupBy === "SECOND_PAGE_TITLE") return secondTitle;
  if (groupBy === "EXP_VARIANT_STRING") return expVariant;

  return pickFirstNonEmpty(
    defaultChannelGroup,
    getSourceMediumLabel(item),
    campaign,
    firstUrlGroup,
    secondUrlGroup,
    firstTitle,
    secondTitle,
    expVariant,
  );
}

/**
 * group ラベルにドメイン辞書を適用すべき groupBy のみを判定する。
 */
export function resolveUaGroupLabelDomain(
  groupBy: UaGroupBy | null,
): "ua.defaultChannelGroup" | undefined {
  return groupBy === "DEFAULT_CHANNEL_GROUP"
    ? "ua.defaultChannelGroup"
    : undefined;
}

function resolveGroupedTimeseriesDate(
  item: Record<string, unknown>,
): string | null {
  return (
    toString(
      item.date ??
        item.currentDate ??
        item.day ??
        item.week ??
        item.month ??
        item.dateKey,
    ) ?? "__all__"
  );
}

function readMetricValue(
  entry: Record<string, unknown>,
  metric: MetricBlueprint,
) {
  const paths = metric.sourcePaths ?? [metric.sourceKey ?? metric.key];
  return readFirstPathNumber({ source: entry, paths }).value;
}

/**
 * grouped rows を date/group/metric の row-based 形式へ整形する。
 *
 * @remarks
 * tool 出力が日付キーを持たない groupedData の場合は `date="__all__"` を補い、
 * renderer 側の bar 集計で扱えるようにする。
 */
export function buildGroupedTimeseriesRows(args: {
  items: unknown[];
  groupBy: UaGroupBy | null;
  metrics: MetricBlueprint[];
  period?: "current" | "previous";
}): VizDatasetRow[] {
  const rows: VizDatasetRow[] = [];

  for (const entry of args.items) {
    if (!isRecord(entry)) continue;
    const date = resolveGroupedTimeseriesDate(entry);
    const group = getUaGroupLabel(entry, args.groupBy);
    if (!date || !group) continue;

    const row: VizDatasetRow = {
      date,
      group,
      ...(args.period ? { period: args.period } : null),
    };

    let hasMetric = false;
    for (const metric of args.metrics) {
      const value = readMetricValue(entry, metric);
      row[metric.key] = value;
      hasMetric = hasMetric || typeof value === "number";
    }
    if (!hasMetric) continue;

    rows.push(row);
  }

  return rows.sort((left, right) => {
    const leftDate = toString(left.date) ?? "";
    const rightDate = toString(right.date) ?? "";
    if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);

    const leftGroup = toString(left.group) ?? "";
    const rightGroup = toString(right.group) ?? "";
    if (leftGroup !== rightGroup) return leftGroup.localeCompare(rightGroup);

    const leftPeriod = toString(left.period) ?? "";
    const rightPeriod = toString(right.period) ?? "";
    return leftPeriod.localeCompare(rightPeriod);
  });
}
